"use client";

import { ImagePlus } from "lucide-react";
import { cn, Text, VStack } from "@/ui";

type ComparisonImageFrameBaseProps = {
  label: string;
  src: string | null;
  alt: string;
};

type ComparisonImageDisplayProps = ComparisonImageFrameBaseProps & {
  src: string;
};

type ComparisonImageUploadProps = ComparisonImageFrameBaseProps & {
  emptyHint: string;
  emptySubhint: string;
  changeHint: string;
  onUploadClick: () => void;
  uploadAriaLabel: string;
};

export type ComparisonImageFrameProps = ComparisonImageDisplayProps | ComparisonImageUploadProps;

function isUploadFrame(props: ComparisonImageFrameProps): props is ComparisonImageUploadProps {
  return "onUploadClick" in props;
}

export function ComparisonImageFrame(props: ComparisonImageFrameProps) {
  const { label, src, alt } = props;
  const isUploadTarget = isUploadFrame(props);

  const frameContent = (
    <>
      {src ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            className="size-full object-cover"
          />
          {isUploadTarget ? (
            <span className="pointer-events-none absolute inset-0 flex items-end justify-center bg-linear-to-t from-black/50 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
              <Text className="text-[10px] font-medium uppercase tracking-[0.12em] text-white">
                {props.changeHint}
              </Text>
            </span>
          ) : null}
        </>
      ) : (
        <VStack className="absolute inset-0 items-center justify-center gap-2 px-3 text-center">
          <ImagePlus
            className="size-6 text-muted-foreground/50"
            aria-hidden
          />
          {isUploadTarget ? (
            <>
              <Text className="text-[10px] font-medium leading-snug text-muted-foreground">
                {props.emptyHint}
              </Text>
              <Text className="text-[10px] leading-snug text-muted-foreground/70">
                {props.emptySubhint}
              </Text>
            </>
          ) : null}
        </VStack>
      )}
    </>
  );

  const frameClassName = cn(
    "relative aspect-3/4 w-full overflow-hidden rounded-sm border bg-muted/20",
    !src && isUploadTarget && "border-dashed border-border/80",
    isUploadTarget &&
      "cursor-pointer transition-colors hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  );

  return (
    <VStack className="min-w-0 flex-1 gap-2">
      <Text className="text-[10px] font-medium uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </Text>
      {isUploadTarget ? (
        <button
          type="button"
          className={cn("group", frameClassName)}
          onClick={props.onUploadClick}
          aria-label={props.uploadAriaLabel}
        >
          {frameContent}
        </button>
      ) : (
        <div className={frameClassName}>{frameContent}</div>
      )}
    </VStack>
  );
}
